import React, { useEffect } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCTooltip, RCCSelectOptionsCard } from "react-common-components";
import { useFieldArray } from "react-hook-form";
import LovAccount from "src/components/Lov/Account";
import LovCostCenter from "src/components/Lov/CostCenter";
import LovOtherAccounts from "src/components/Lov/OtherAccounts";
import LovProductLine from "src/components/Lov/ProductLine";
import LovSalesChannel from "src/components/Lov/SalesChannel";
import LovSalesDeduction from "src/components/Lov/SalesDeduction";
import LovVariableCosts from "src/components/Lov/VariableCosts";
import LovVariableExpenses from "src/components/Lov/VariableExpenses";

import { useTranslation } from "src/context/Translations/context";
import { v4 } from "uuid";
import { IComponentCompositionValuesOfLine, IOftbm003Item } from "./interface";

const CompositionValuesOfLine: React.FC<IComponentCompositionValuesOfLine> = ({
	lines,
	control,
	register,
	budgetPlanId,
	setValue,
	origin,
	useApportionment
}) => {
	const { translate } = useTranslation();

	const { 
		fields: internalLines, 
		remove: removeLines,
		append: appendLines
	} = useFieldArray({
		control,
		name: "oftbm003",
		shouldUnregister: true
	});

	const addLine = () => {
		appendLines({ id: v4(), ofm003Id: 0 } as IOftbm003Item);
	};

	const removeLine = (index: number) => {			
		removeLines(index);
	};

	useEffect(() => {
		appendLines(lines.map((line) => ({
			...line,
			id: v4()
		})));
	}, []);

	const getHierarchy1 = () => {
		switch (origin){
		case 1:
			return 3;
		case 2:
			return 3;
		case 3:
			return 4;
		case 4:
			return 3;
		case 5:
			return 7;
		case 6:
			return 7;
		case 91:
			return 7;
		default:
			return 0;
		}
	};

	const getHierarchy2 = () => {
		switch (origin){
		case 1:
			return 4;
		case 2:
			return 4;
		case 3:
			return 10;
		case 4:
			return 4;
		case 5:
			return 8;
		case 6:
			return 9;
		case 91:
			return 9;
		default:
			return 0;
		}
	};

	const getHierarchy3 = () => {
		switch (origin){
		case 2:
			return 6;
		case 4:
			return 5;
		default:
			return 0;
		}
	};

	const returnLov1 = (index: number, value?: string, label?: string, corporativeValue: string = "") => {
		switch (origin)
		{
		case 1:
			return <LovSalesChannel 
				control={control}
				name={`oftbm003[${index}].oftbm004[0].ofm004HierarquiaId`}
				label={translate("Canal de venda")}
				extraParametersOnLoad={{
					budgetPlanId
				}}
				defaultValue={value && label ? {value, label} : undefined}
				setValue={setValue}
			/>;
		case 2:
			return <LovSalesChannel 
				control={control}
				name={`oftbm003[${index}].oftbm004[0].ofm004HierarquiaId`}
				label={translate("Canal de venda")}
				extraParametersOnLoad={{
					budgetPlanId
				}}
				defaultValue={value && label ? {value, label} : undefined}
				setValue={setValue}
			/>;
		case 3:
			return <LovProductLine 
				control={control}
				name={`oftbm003[${index}].oftbm004[0].ofm004HierarquiaId`}
				label={translate("Linha de produto")}
				extraParametersOnLoad={{
					budgetPlanId
				}}
				defaultValue={value && label ? {value, label} : undefined}
				setValue={setValue}
			/>;
		case 4:
			return <LovSalesChannel 
				control={control}
				name={`oftbm003[${index}].oftbm004[0].ofm004HierarquiaId`}
				label={translate("Canal de venda")}
				extraParametersOnLoad={{
					budgetPlanId
				}}
				defaultValue={value && label ? {value, label} : undefined}
				setValue={setValue}
			/>;
		case 5:
			return <RCCGrid container item spacing={useApportionment ? 1 : undefined}>
				<RCCGrid container item xs={useApportionment ? 8 : 12}>
					<LovCostCenter 
						control={control}
						name={`oftbm003[${index}].oftbm004[0].ofm004HierarquiaId`}
						label={translate("Centros de custo")}
						extraParametersOnLoad={{
							budgetPlanId
						}}
						defaultValue={value && label ? {value, label} : undefined}
						setValue={setValue}
					/>
				</RCCGrid>
				{useApportionment && (
					<RCCGrid container item xs={4} justifyContent="flex-end">
						<RCCSelectOptionsCard
							control={control}
							name={`oftbm003[${index}].oftbm004[0].ofm004Corporativo`}
							defaultValue={corporativeValue}
							options={[{
								label: translate("Todos"),
								value: ""
							},{
								label: translate("Sim"),
								value: "S"
							},{
								label: translate("Não"),
								value: "N"
							}]}
							label={translate("Corporativo")}
							styleInline=">div{ width: fit-content; }"
						/>
					</RCCGrid>
				)}
			</RCCGrid>;
		case 6:
			return <LovCostCenter 
				control={control}
				name={`oftbm003[${index}].oftbm004[0].ofm004HierarquiaId`}
				label={translate("Centros de custo")}
				extraParametersOnLoad={{
					budgetPlanId
				}}
				defaultValue={value && label ? {value, label} : undefined}
				setValue={setValue}
			/>;
		case 91:
			return <LovCostCenter 
				control={control}
				name={`oftbm003[${index}].oftbm004[0].ofm004HierarquiaId`}
				label={translate("Centros de custo")}
				extraParametersOnLoad={{
					budgetPlanId
				}}
				defaultValue={value && label ? {value, label} : undefined}
				setValue={setValue}
			/>;
		default:
			return null;
		}
	};

	const returnLov2 = (index: number, value?: string, label?: string) => {
		switch (origin)
		{
		case 1:
			return <LovProductLine 
				control={control}
				name={`oftbm003[${index}].oftbm004[1].ofm004HierarquiaId`}
				label={translate("Linha de produto")}
				extraParametersOnLoad={{
					budgetPlanId
				}}
				defaultValue={value && label ? {value, label} : undefined}
				setValue={setValue}
			/>;
		case 2:
			return <LovProductLine 
				control={control}
				name={`oftbm003[${index}].oftbm004[1].ofm004HierarquiaId`}
				label={translate("Linha de produto")}
				extraParametersOnLoad={{
					budgetPlanId
				}}
				defaultValue={value && label ? {value, label} : undefined}
				setValue={setValue}
			/>;
		case 3:
			return <LovVariableCosts
				control={control}
				name={`oftbm003[${index}].oftbm004[1].ofm004HierarquiaId`}
				label={translate("Custos variáveis")}
				extraParametersOnLoad={{
					budgetPlanId
				}}
				defaultValue={value && label ? {value, label} : undefined}
				setValue={setValue}
			/>;
		case 4:
			return <LovProductLine 
				control={control}
				name={`oftbm003[${index}].oftbm004[1].ofm004HierarquiaId`}
				label={translate("Linha de produto")}
				extraParametersOnLoad={{
					budgetPlanId
				}}
				defaultValue={value && label ? {value, label} : undefined}
				setValue={setValue}
			/>;
		case 5:
			return <LovAccount 
				control={control}
				name={`oftbm003[${index}].oftbm004[1].ofm004HierarquiaId`}
				label={translate("Contas")}
				extraParametersOnLoad={{
					budgetPlanId
				}}
				defaultValue={value && label ? {value, label} : undefined}
				setValue={setValue}
			/>;
		case 6:
			return <LovOtherAccounts 
				control={control}
				name={`oftbm003[${index}].oftbm004[1].ofm004HierarquiaId`}
				label={translate("Outras contas")}
				extraParametersOnLoad={{
					budgetPlanId
				}}
				defaultValue={value && label ? {value, label} : undefined}
				setValue={setValue}
			/>;
		case 91:
			return <LovOtherAccounts 
				control={control}
				name={`oftbm003[${index}].oftbm004[1].ofm004HierarquiaId`}
				label={translate("Outras contas")}
				extraParametersOnLoad={{
					budgetPlanId
				}}
				defaultValue={value && label ? {value, label} : undefined}
				setValue={setValue}
			/>;
		default:
			return null;
		}
	};

	const returnLov3 = (index: number, value?: string, label?: string) => {
		switch (origin)
		{
		case 2:
			return <LovSalesDeduction
				control={control}
				name={`oftbm003[${index}].oftbm004[2].ofm004HierarquiaId`}
				label={translate("Deduções de vendas")}
				extraParametersOnLoad={{
					budgetPlanId
				}}
				defaultValue={value && label ? {value, label} : undefined}
				setValue={setValue}
			/>;
		case 4:
			return <LovVariableExpenses
				control={control}
				name={`oftbm003[${index}].oftbm004[2].ofm004HierarquiaId`}
				label={translate("Despesas variáveis")}
				extraParametersOnLoad={{
					budgetPlanId
				}}
				defaultValue={value && label ? {value, label} : undefined}
				setValue={setValue}
			/>;
		default:
			return null;
		}
	};

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
				alignItems="center"
				justifyContent="center"
			>
				<RCCGrid
					container
					item
				>
					<RCCGrid
						container
						item
						justifyContent="flex-end"
					>
						<RCCTooltip
							color="purple"
							text={translate("Adicionar valor")}
							position="left"
						>
							<RCCButton
								color="purple"
								icon={{
									name: "plus"
								}}
								layout="isRounded"
								size="md"
								onClick={addLine}
							/>
						</RCCTooltip>
					</RCCGrid>
				</RCCGrid>
			
				<RCCGrid
					container
					item
					xs={12}
					sm={10}
				>
						
					{internalLines.length === 0 ? (
						<RCCAlert
							color="grey"
							title={translate("Nenhum valor adicionado")}
							description={translate("Aperte no botão acima para adicionar")}
						/>
					) : internalLines.map((miles: any, index: number) => (
						<RCCGrid
							container
							item
							alignItems="center"
							styleInline="position: relative;"
							key={miles.id}
							style={{ 
								borderBottom: "1px solid var(--border-color)",
								borderTop: index === 0 ? "1px solid var(--border-color)" : "none"
							}}
						>		
							<input 
								type="text"
								value={miles.ofm003Id}
								{...register(`oftbm003[${index}].ofm003Id`)} 
								style={{ display: "none" }} 
								readOnly
							/>								
							<RCCGrid
								container
								item
								xs={12}
								sm={10}
							>
								<RCCSelectOptionsCard
									name={`oftbm003[${index}].ofm003Operador`}
									control={control}
									defaultValue={miles.ofm003Operador ? miles.ofm003Operador.toString() : "1"}
									options={[
										{
											value: "1",
											label: `${translate("Somar")} (+)`
										},
										{
											value: "2",
											label: `${translate("Subtrair")} (-)`
										}
									]}
									label={translate("Operador")}
								/>
							</RCCGrid>

							<RCCGrid
								container
								item
								xs={12}
								sm={10}
							>
								<input 
									type="text"
									value={miles.oftbm004 && miles.oftbm004.length > 0 ? miles.oftbm004[0].ofm004Id : 0}
									{...register(`oftbm003[${index}].oftbm004[0].ofm004Id`)} 
									style={{ display: "none" }} 
									readOnly
								/>	
								<input 
									type="text"
									value={miles.oftbm004 && miles.oftbm004.length > 0 ? miles.oftbm004[0].ofm004Hierarquia : getHierarchy1()}
									{...register(`oftbm003[${index}].oftbm004[0].ofm004Hierarquia`)} 
									style={{ display: "none" }} 
									readOnly
								/>		
								{returnLov1(index, 
									miles.oftbm004 && miles.oftbm004.length > 0 ? miles.oftbm004[0].ofm004HierarquiaId : undefined, 
									miles.oftbm004 && miles.oftbm004.length > 0 ? miles.oftbm004[0].ofm004Descricao : undefined,
									miles.oftbm004 && miles.oftbm004.length > 0 ? miles.oftbm004[0].ofm004Corporativo : ""
								)}
							</RCCGrid>

							<RCCGrid
								container
								item
								xs={12}
								sm={10}
							>
								<input 
									type="text"
									value={miles.oftbm004 && miles.oftbm004.length > 0 ? miles.oftbm004[1].ofm004Id : 0}
									{...register(`oftbm003[${index}].oftbm004[1].ofm004Id`)} 
									style={{ display: "none" }} 
									readOnly
								/>
								<input 
									type="text"
									value={miles.oftbm004 && miles.oftbm004.length > 0 ? miles.oftbm004[1].ofm004Hierarquia : getHierarchy2()}
									{...register(`oftbm003[${index}].oftbm004[1].ofm004Hierarquia`)} 
									style={{ display: "none" }} 
									readOnly
								/>	
								{returnLov2(index, 
									miles.oftbm004 && miles.oftbm004.length > 0 ? miles.oftbm004[1].ofm004HierarquiaId : undefined, 
									miles.oftbm004 && miles.oftbm004.length > 0 ? miles.oftbm004[1].ofm004Descricao : undefined
								)}
							</RCCGrid>
								
							{(origin === 2 || origin === 4) && (
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
								>
									<input 
										type="text"
										value={miles.oftbm004 && miles.oftbm004.length > 0 ? miles.oftbm004[2].ofm004Id : 0}
										{...register(`oftbm003[${index}].oftbm004[2].ofm004Id`)} 
										style={{ display: "none" }} 
										readOnly
									/>
									<input 
										type="text"
										value={miles.oftbm004 && miles.oftbm004.length > 0 ? miles.oftbm004[2].ofm004Hierarquia : getHierarchy3()}
										{...register(`oftbm003[${index}].oftbm004[2].ofm004Hierarquia`)} 
										style={{ display: "none" }} 
										readOnly
									/>
									{returnLov3(index, 
										miles.oftbm004 && miles.oftbm004.length > 0 ? miles.oftbm004[2].ofm004HierarquiaId : undefined, 
										miles.oftbm004 && miles.oftbm004.length > 0 ? miles.oftbm004[2].ofm004Descricao : undefined
									)}
								</RCCGrid>
							)}
							
							<RCCButton 
								color="red"
								icon={{
									name: "trash"
								}}
								size="md"
								layout="isRounded"
								onClick={() => removeLine(index)}
								styleInline="position: absolute; top: 4px; right: 0;"
							/>
						</RCCGrid>											
					))}
				</RCCGrid>
			</RCCGrid>

		</>
	);
};

export default CompositionValuesOfLine;
